import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Check, Loader2 } from 'lucide-react';
import { formatIndianCurrency } from '../../utils/currency';

const featureText = (f) => {
  if (f.limit_value === -1) return `${f.name}: Unlimited`;
  if (f.limit_value != null) return `${f.name}: ${f.limit_value}`;
  return f.name;
};

// Explicit confirmation shown before any plan (trial or paid) is activated
export const PlanConfirmDialog = ({
  open, onOpenChange, plan, mode, trialDays, billingCycle, onConfirm, loading, children,
}) => {
  if (!plan) return null;
  const features = (plan.features || []).filter((f) => f.is_enabled);
  const monthly = formatIndianCurrency((plan.price_monthly || 0) / 100);
  const cyclePrice = billingCycle === 'annual'
    ? `${formatIndianCurrency((plan.price_annual || 0) / 100)}/year`
    : `${monthly}/month`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="plan-confirm-dialog">
        <DialogHeader>
          <DialogTitle data-testid="plan-confirm-title">Confirm {plan.name} plan</DialogTitle>
          <DialogDescription>
            {mode === 'trial'
              ? <>Free trial · <span data-testid="plan-confirm-duration">{trialDays} days</span></>
              : <>Billed {billingCycle === 'annual' ? 'yearly' : 'monthly'} · {cyclePrice} + GST</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {children}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Features included</h4>
            <ul className="space-y-1.5 text-sm text-slate-600 max-h-48 overflow-y-auto" data-testid="plan-confirm-features">
              {features.map((f) => (
                <li key={f.feature_key} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />{featureText(f)}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="plan-confirm-renewal">
            {mode === 'trial'
              ? <>After your trial, this plan renews at {monthly}/month. You will be asked to pay at that point — nothing is charged automatically.</>
              : <>This plan costs {cyclePrice} + GST. Nothing is charged automatically.</>}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="plan-confirm-cancel">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="plan-confirm-submit">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activating...</> : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanConfirmDialog;
